<template>
  <form @submit="handleSubmit">
    <card>
      <h5 class="section-title">Basic Information</h5>
      <p class="section-description">
        Enter the basic details about the product
      </p>
      <br />
      <div class="row">
        <div class="col-6">
          <single-select
            label="Product Type"
            placeholder="Select Product Type"
            id="productType"
            :value="forms.productType"
            :options="productTypeOptions"
            @change="(val) => handleChangeInput('productType', val)"
            :error="(errors && errors.productType) || null"
            :disabled="!!catalogId"
          >
            <template #side-icon>
              <i class="fa fa-chevron-down"></i>
            </template>
          </single-select>
        </div>
        <div class="col-6">
          <single-select
            label="Brand"
            placeholder="Select Brand"
            id="brand"
            :value="forms.productDetail.brand"
            :options="brandOptions"
            @change="(val) => handleChangeInput('productDetail.brand', val)"
            :disabled="!!catalogId"
            :error="
              (errors && errors.productDetail && errors.productDetail.brand) ||
              null
            "
          >
            <template #side-icon>
              <i class="fa fa-chevron-down"></i>
            </template>
          </single-select>
          <div v-if="showOtherBrandInput" style="height: 56px">
            <input-text
              placeholder="Enter other brand name"
              id="otherBrandName"
              :value="forms.otherBrand"
              @change="(val) => handleChangeInput('otherBrand', val)"
              :disabled="!!catalogId"
              :error="(errors && errors.otherBrand) || null"
            />
          </div>
        </div>
        <div class="col-6 mt-3">
          <input-text
            label="Model"
            placeholder="Enter Product Model"
            id="productModel"
            :value="forms.productDetail.model"
            @change="(val) => handleChangeInput('productDetail.model', val)"
            :error="
              (errors && errors.productDetail && errors.productDetail.model) ||
              null
            "
            :disabled="!!catalogId"
          />
        </div>
        <div class="col-6 mt-3">
          <input-text
            label="Production Year"
            placeholder="eg. 2025"
            id="productionYear"
            :value="forms.year"
            type="number"
            @change="(val) => handleChangeInput('year', val)"
            :error="(errors && errors.year) || null"
          />
        </div>
        <div class="col-12 mt-3">
          <single-select
            label="Category"
            placeholder="Select Category"
            id="category"
            :value="forms.catalogCategory"
            :options="categoryOptions"
            @change="(val) => handleChangeInput('catalogCategory', val)"
            :error="(errors && errors.catalogCategory) || null"
          >
            <template #side-icon>
              <i class="fa fa-chevron-down"></i>
            </template>
          </single-select>
        </div>
      </div>
      <hr class="my-4" />
      <!-- Specification -->
      <div v-if="forms.productType">
        <h5 class="section-title">Technical Specifications</h5>
        <p class="section-description">
          Define the technical details and hardware specifications
        </p>
      </div>
      <div v-if="[1, 2].includes(Number(forms.productType))">
        <laptop-and-cpu
          :forms="forms"
          :errors="errors"
          @handleChangeInput="handleChangeInput"
          @handleChangeStorage="handleChangeStorage"
          @handleChangeStorageCapacity="handleChangeStorageCapacity"
        />
      </div>
      <div v-if="forms.productType == 3">
        <monitor
          :forms="forms"
          :errors="errors"
          @handleChangeInput="handleChangeInput"
        />
      </div>
      <div v-if="forms.productType == 4">
        <printer
          :forms="forms"
          :errors="errors"
          @handleChangeInput="handleChangeInput"
        />
      </div>
      <div v-if="forms.productType == '5' || forms.productType == '6'">
        <smartphone-and-tablet
          :forms="forms"
          :errors="errors"
          @handleChangeInput="handleChangeInput"
        />
      </div>
      <hr class="my-4" />
      <div class="insurance-wrapper">
        <div class="d-flex align-items-center mb-3" style="gap: 0.5rem">
          <div>
            <img
              src="~@/assets/icon/shield-verified-green.svg"
              alt="verified"
              width="24"
            />
          </div>
          <h5 class="section-title mb-0">Insurance Coverage</h5>
        </div>
        <div>
          <div style="width: 50%">
            <input-currency
              id="sum-insured"
              label="Sum Insured"
              @change="
                (val) => handleChangeInput('sumInsurance', val, 'number')
              "
              :value="forms.sumInsurance"
              :showPrefixLabel="true"
              placeholder="0"
            >
              <template #prefix-label>Rp</template>
            </input-currency>
          </div>
          <p class="hint">Insurance coverage amount for the product</p>
        </div>
      </div>
      <hr class="my-4" />
      <div>
        <h5 class="section-title">Product Images</h5>
        <p class="section-description">
          Upload high-quality images of the products
        </p>
        <br />
        <div>
          <div class="d-flex" style="gap: 20px">
            <upload-image
              :value="forms.photo[0]"
              @filePicked="handleFilePicked($event, 0)"
            />
            <upload-image
              :value="forms.photo[1]"
              @filePicked="handleFilePicked($event, 1)"
            />
            <upload-image
              :value="forms.photo[2]"
              @filePicked="handleFilePicked($event, 2)"
            />
            <upload-image
              :value="forms.photo[3]"
              @filePicked="handleFilePicked($event, 3)"
            />
          </div>
        </div>
        <small class="text-danger m-0">{{
          (errors && errors.photo) || ""
        }}</small>
        <div class="mt-4">
          <input-textarea
            id="description"
            :value="forms.description"
            placeholder="Enter detailed product description..."
            label="Product Description"
            :charLimit="500"
            @change="(val) => handleChangeInput('description', val)"
          />
        </div>
      </div>
      <hr class="my-4" />

      <!-- Tenor form -->
      <div>
        <div class="d-flex justify-content-between align-items-center mb-3">
          <div class="d-flex align-items-center" style="gap: 0.5rem">
            <img src="~@/assets/icon/calendar-blue.svg" alt="calendar icon" />
            <strong class="m-0">Rental Tenor</strong>
          </div>
          <custom-button @click="addTenor"> + Add Tenor </custom-button>
        </div>
        <card v-for="(item, index) in forms.tenor" :key="index" class="mb-3">
          <div class="d-flex align-items-center justify-content-between">
            <strong>Tenor #{{ index + 1 }}</strong>
            <div
              v-if="forms.tenor.length > 1"
              @click="removeTenor(index)"
              class="text-danger"
            >
              <i class="fa fa-trash" style="cursor: pointer"></i>
            </div>
          </div>
          <div class="row mt-4">
            <div class="col-6">
              <input-text
                label="Tenor"
                placeholder="Duration"
                id="tenorDuration"
                type="number"
                :value="item.duration"
                showSuffixLabel
                @change="
                  (val) => handleChangeTenorsInput('duration', val, index)
                "
                :error="
                  (errors &&
                    errors.tenor &&
                    errors.tenor[index] &&
                    errors.tenor[index].duration) ||
                  null
                "
              >
                <template #suffix-label>month</template>
              </input-text>
            </div>
            <div class="col-6">
              <input-currency
                label="Rent Fee"
                placeholder="0"
                id="rentFee"
                type="text"
                :value="item.rentFee"
                showSuffixLabel
                showPrefixLabel
                @change="
                  (val) =>
                    handleChangeTenorsInput('rentFee', val, index, 'number')
                "
                :error="
                  (errors &&
                    errors.tenor &&
                    errors.tenor[index] &&
                    errors.tenor[index].rentFee) ||
                  null
                "
              >
                <template #suffix-label>month</template>
                <template #prefix-label>Rp</template>
              </input-currency>
            </div>
          </div>
          <div class="row mt-4">
            <div class="col-6">
              <single-select
                label="Condition"
                placeholder="Select Condition"
                id="tenorCondition"
                :value="item.condition"
                :options="conditionOptions"
                @change="
                  (val) => handleChangeTenorsInput('condition', val, index)
                "
                :error="
                  (errors &&
                    errors.tenor &&
                    errors.tenor[index] &&
                    errors.tenor[index].condition) ||
                  null
                "
              >
                <template #side-icon>
                  <i class="fa fa-chevron-down"></i>
                </template>
              </single-select>
            </div>
            <div class="col-6">
              <single-select
                label="Status"
                placeholder="Status"
                id="tenorStatus"
                :value="item.status"
                :options="tenorStatusOptions"
                @change="(val) => handleChangeTenorsInput('status', val, index)"
                :error="
                  (errors &&
                    errors.tenor &&
                    errors.tenor[index] &&
                    errors.tenor[index].status) ||
                  null
                "
              >
                <template #side-icon>
                  <i class="fa fa-chevron-down"></i>
                </template>
              </single-select>
            </div>
          </div>
        </card>
      </div>
    </card>
    <div class="mt-3 d-flex justify-content-end" style="gap: 10px">
      <router-link
        :to="'/catalog/bom/' + catalogId + '?unit=' + isUnit"
        v-if="
          currentData &&
          (currentData.productType == '1' || currentData.productType == '2')
        "
        class="btn btn-info btn-sm d-flex align-items-center"
      >
        <p class="m-0">View BOM ({{ totalBom }})</p>
      </router-link>
      <custom-button type="button" @click="handleSubmit"> Save </custom-button>
    </div>
  </form>
</template>

<script>
import { CustomButton } from "../../Button";
import { Card } from "../../Layout";
import {
  SingleSelect,
  InputText,
  InputTextarea,
  InputRadio,
  InputCurrency,
} from "../../Input";
import UploadImage from "./UploadImage.vue";
import { authMixin, parserMixin } from "../../../mixins";
import {
  validate,
  required,
  minLength,
  arrayOfObject,
} from "../../../utils/objectValidation";
import swal from "sweetalert";
import {
  LaptopAndCpu,
  Monitor,
  Printer,
  SmartphoneAndTablet,
} from "./TechSpec";

const dataTemplate = {
  productType: "",
  catalogCategory: "",
  sumInsurance: 0,
  photo: [null, null, null, null],
  tenor: [
    {
      duration: "",
      rentFee: 0,
      condition: "",
      status: "",
    },
  ],
  productDetail: {
    brand: "",
    model: "",
  },
  description: "",
  year: "",
  otherBrand: "",
};

const validationTemplate = {
  productType: [required()],
  catalogCategory: [required()],
  // sumInsurance: [required()],
  // description: [
  //   required(),
  //   minLength(10, "Description must be at least 10 characters"),
  // ],
  year: [required()],
  productDetail: {
    brand: [required()],
    model: [required()],
  },
  photo: [required("You must upload at least one image"), minLength(1)],
  tenor: arrayOfObject({
    duration: [required()],
    rentFee: [required()],
    condition: [required()],
    status: [required()],
  }),
};

export default {
  name: "ProductForm",
  components: {
    CustomButton,
    Card,
    SingleSelect,
    InputText,
    UploadImage,
    InputTextarea,
    InputRadio,
    InputCurrency,
    LaptopAndCpu,
    Monitor,
    Printer,
    SmartphoneAndTablet,
  },
  props: {
    currentData: {
      type: Object,
      default: null,
    },
    catalogId: {
      type: Number,
      default: 0,
    },
  },
  mixins: [authMixin, parserMixin],
  data() {
    return {
      spaceRegex:  /[\u00A0\uFEFF\u202F]|\s{2,}/g,
      totalBom: 0,
      isUnit: true,
      forms: { ...dataTemplate },
      productTypeOptions: [],
      brandOptions: [],
      categoryOptions: [],
      memoryTypeOptions: [
        { label: "Removable", value: "removable" },
        { label: "Onboard", value: "onboard" },
      ],
      conditionOptions: [],
      tenorStatusOptions: [
        {
          value: 1,
          label: "Active",
        },
        {
          value: 0,
          label: "Inactive",
        },
      ],
      dataValidation: { ...validationTemplate },
      errors: null,
      showOtherBrandInput: false,
    };
  },
  methods: {
    handleFilePicked(fileData, index) {
      this.forms.photo.splice(index, 1, fileData);
      if (fileData) {
        if (this.errors && this.errors.photo) {
          this.errors.photo = null;
        }
      }
    },
    updateNested(obj, path, value) {
      const keys = path.split(".");
      let current = obj;
      for (let i = 0; i < keys.length - 1; i++) {
        if (!(keys[i] in current) || typeof current[keys[i]] !== "object") {
          current[keys[i]] = {};
        }
        current = current[keys[i]];
      }
      current[keys[keys.length - 1]] = value;
    },
    handleChangeInput(field, val, dataType = "string") {
      let newVal = val;
      // Show other brand input if brand is other
      if (field === "productDetail.brand" && val.toLowerCase() === "other") {
        this.showOtherBrandInput = true;
      }

      if (field === "productDetail.brand" && val.toLowerCase() !== "other") {
        this.showOtherBrandInput = false;
        this.forms.otherBrand = ""

      }

      if (field === "productType") {
        this.adjustDataForDifferentProductType(newVal);
      }

      // Checking data type for number
      if (dataType === "number") {
        newVal = typeof val === "number" ? val : Number(val);
        if (isNaN(newVal)) {
          newVal = 0;
        }
      }
      this.updateNested(this.forms, field, newVal);

      // Reset specific errors
      if (field === "storageType") {
        this.resetErrorStorage(newVal);
      }
      if (field === "ramType") {
        this.resetRamErrors(newVal);
      }
    },
    handleChangeStorage(val) {
      const capacity =
        (this.forms.productDetail &&
          this.forms.productDetail.storage.length > 0 &&
          this.forms.productDetail.storage[0].capacity) ||
        "";
      if (val === "SSD+HDD") {
        this.forms.productDetail.storage = [
          { type: "SSD", capacity: capacity },
          { type: "HDD", capacity: "" },
        ];
      } else {
        this.forms.productDetail.storage = [{ type: val, capacity: capacity }];
      }
      this.resetErrorStorage(val);
    },
    resetErrorStorage(val) {
      if (val && this.forms.storageType) {
        if (
          this.errors &&
          this.errors.productDetail &&
          this.errors.productDetail.storage
        ) {
          this.errors.productDetail.storage = null;
        }
        if (this.errors && this.errors.storageType) {
          this.errors.storageType = null;
        }
      }
    },
    resetRamErrors(val) {
      if (val && this.forms.productDetail.ram) {
        if (
          this.errors &&
          this.errors.productDetail &&
          this.errors.productDetail.ram
        ) {
          this.errors.productDetail.ram = null;
        }
        if (this.errors && this.errors.ramType) {
          this.errors.ramType = null;
        }
      }
    },
    handleChangeStorageCapacity(val, index) {
      if (
        this.forms.productDetail.storage &&
        this.forms.productDetail.storage.length > 0
      ) {
        this.forms.productDetail.storage[index].capacity = val;
      } else {
        this.forms.productDetail.storage = [{ capacity: val, type: "" }];
      }
    },
    handleChangeTenorsInput(field, val, index, dataType = "string") {
      let newVal = val;
      if (dataType === "number") {
        newVal = Number(val);
        if (isNaN(newVal)) {
          newVal = 0;
        }
      }
      this.updateNested(this.forms.tenor[index], field, newVal);
    },
    removeTenor(index) {
      this.forms.tenor.splice(index, 1);
    },
    addTenor() {
      this.forms.tenor.push({
        duration: "",
        rentFee: 0,
        condition: "",
        status: "",
      });
    },
    adjustDataForDifferentProductType(val) {
      // Prevent mutable data
      this.forms = { ...dataTemplate };
      this.dataValidation = { ...validationTemplate };

      // For Laptop and PC
      if (["1", "2"].includes(val)) {
        this.forms.productDetail = {
          ...this.forms.productDetail,
          processor: "",
          ram: "",
          storage: [],
          vga: "",
          screen: "",
        };
        this.forms.ramType = "";
        this.forms.storageType = "";

        // Adjust validation
        this.dataValidation.ramType = [
          required("RAM and RAM slot type must be fill"),
        ];
        this.dataValidation.storageType = [
          required("Storage type, capacity and slot type must be fill"),
        ];
        this.dataValidation.productDetail = {
          ...this.dataValidation.productDetail,
          processor: [required()],
          ram: [required("RAM and RAM slot type must be fill"), minLength(1)],
          storage: arrayOfObject({
            type: [required("Storage type, capacity and slot type must be fill")],
            capacity: [required("Storage type, capacity and slot type must be fill")],
          }),
          vga: [required()],
          screen: [required()],
        };
      }

      // For Monitor
      if (val == "3") {
        this.forms.productDetail = {
          ...this.forms.productDetail,
          displaySize: "",
          displayType: "",
          portVga: "",
          portDvid: "",
          portHdmi: "",
        };

        // Adjust validation
        this.dataValidation.productDetail = {
          ...this.dataValidation.productDetail,
          displaySize: [required()],
          displayType: [required()],
          portVga: [required()],
          portDvid: [required()],
          portHdmi: [required()],
        };
      }

      // for printer
      if (val == "4") {
        this.forms.productDetail = {
          ...this.forms.productDetail,
          feature: "",
          colorTech: "",
        };

        // Adjust validation
        this.dataValidation.productDetail = {
          ...this.dataValidation.productDetail,
          feature: [required()],
          colorTech: [required()],
        };
      }

      // for smartphone and tablet
      if (val == "5" || val == "6") {
        this.forms.productDetail = {
          ...this.forms.productDetail,
          screen: "",
          processor: "",
          ram : "",
          storage : "",
          mainCamera: "",
          frontCamera: "",
          battery: "",
          feature: "",
        };

        // Adjust Validation
        this.dataValidation.productDetail = {
          ...this.dataValidation.productDetail,
          screen: [required()],
          processor: [required()],
          ram: [required()],
          storage: [required()],
          mainCamera: [required()],
          frontCamera: [required()],
          battery: [required()],
          feature: [required()],
        };
      }
    },
    handleSubmit() {
      // console.log(this.forms)
      // event.preventDefault();
      const currentData = {
        ...this.forms,
        otherBrand : (this.forms.otherBrand || '').replace(this.spaceRegex, ' ').trim(),
        productDetail: {
          ...this.forms.productDetail,
          model : (this.forms.productDetail.model || '').replace(this.spaceRegex, ' ').trim(),
          processor : (this.forms.productDetail.processor || '').replace(this.spaceRegex, ' ').trim(),
          vga : (this.forms.productDetail.vga || '').replace(this.spaceRegex, ' ').trim(),
          displaySize : (this.forms.productDetail.displaySize || '').replace(this.spaceRegex, ' ').trim(),
          feature : (this.forms.productDetail.feature || '').replace(this.spaceRegex, ' ').trim(),
          colorTech : (this.forms.productDetail.colorTech || '').replace(this.spaceRegex, ' ').trim(),
          screen : (this.forms.productDetail.screen || '').replace(this.spaceRegex, ' ').trim(),
          mainCamera : (this.forms.productDetail.mainCamera || '').replace(this.spaceRegex, ' ').trim(),
          frontCamera : (this.forms.productDetail.frontCamera || '').replace(this.spaceRegex, ' ').trim(),
          battery : (this.forms.productDetail.battery || '').replace(this.spaceRegex, ' ').trim(),
        },
      };

      if (currentData.productType == "1" || currentData.productType == "2") {
        if (currentData.productDetail.storage.length == 0) {
          currentData.productDetail.storage = [{ type: "", capacity: "" }];
        }
      }

      const validation = { ...this.dataValidation };
      currentData.photo = currentData.photo.filter((item) => item !== null);

      // Add validation for other brand
      if (this.showOtherBrandInput) {
        validation.otherBrand = [required("Please enter the other brand name")];
      }

      const validationResult = validate(currentData, validation);
      if (!validationResult.isValid) {
        this.errors = validationResult.errors;
        swal("Error", "Please fix the validation errors.", "error", {
          button: "Ok",
        });
        return;
      }

      // Adjust sent Payload
      let sentData = {
        ...this.forms,
        otherBrand : (this.forms.otherBrand || '').replace(this.spaceRegex, ' ').trim(),
        productDetail: {
          ...this.forms.productDetail,
           model : (this.forms.productDetail.model || '').replace(this.spaceRegex, ' ').trim(),
          processor : (this.forms.productDetail.processor || '').replace(this.spaceRegex, ' ').trim(),
          vga : (this.forms.productDetail.vga || '').replace(this.spaceRegex, ' ').trim(),
          displaySize : (this.forms.productDetail.displaySize || '').replace(this.spaceRegex, ' ').trim(),
          feature : (this.forms.productDetail.feature || '').replace(this.spaceRegex, ' ').trim(),
          colorTech : (this.forms.productDetail.colorTech || '').replace(this.spaceRegex, ' ').trim(),
          screen : (this.forms.productDetail.screen || '').replace(this.spaceRegex, ' ').trim(),
          mainCamera : (this.forms.productDetail.mainCamera || '').replace(this.spaceRegex, ' ').trim(),
          frontCamera : (this.forms.productDetail.frontCamera || '').replace(this.spaceRegex, ' ').trim(),
          battery : (this.forms.productDetail.battery || '').replace(this.spaceRegex, ' ').trim(),
        },
      };

      // Adjust image data
      sentData.photo = sentData.photo.map((item) => {
        if (item) return item;
        return {
          imgFileName: "",
          imgBase64: "",
          imgUrl:
            "https://asani-imagestorage.s3-ap-southeast-1.amazonaws.com/logo_upload+product/template_product.jpg",
          photoId: 0,
        };
      });

      sentData.productTypeString = this.productTypeOptions.find(
        (item) => item.value == sentData.productType
      ).label;

      // Assign other brand name if selected
      if (this.showOtherBrandInput) {
        sentData.productDetail.brand = sentData.otherBrand;
      }
      delete sentData.otherBrand;

      // Set name based on brand and model
      sentData.name = `${sentData.productDetail.brand} ${sentData.productDetail.model}`;
      sentData.name = sentData.name.replace(this.spaceRegex, ' ')

      // Adjust tenor status data
      sentData.tenor = sentData.tenor.map((item) => {
        const statusData = this.tenorStatusOptions.find(
          (status) => status.value == item.status
        );
        return {
          ...item,
          status: {
            status: statusData.value,
            statusName: statusData.label,
          },
        };
      });

      sentData.isUnit = true;
      sentData.productType = Number(sentData.productType);

      this.$emit("submit", sentData);
    },
    adjustNonLaptopCPUData(data) {
      delete data.productDetail.processor;
      delete data.productDetail.ram;
      delete data.productDetail.storage;
      delete data.productDetail.vga;
      delete data.productDetail.screen;
      delete data.catalogCategory;
    },

    // API Calls
    async getCategory() {
      try {
        const response = await this.$http.get(process.env.GET_CATALOG_CREATE, {
          headers: {
            "Asani-Token": localStorage.getItem("jwt"),
          },
        });
        if (response.data.status !== 200) {
          swal("Sorry", response.data.message, "error", {
            button: "Ok",
          });
          return;
        }
        const responseData = response.data.content;
        this.productTypeOptions = responseData.catalogType.map((item) => ({
          label: item.statusName,
          value: item.status,
        }));
        this.categoryOptions = responseData.catalogCategory.map((item) => ({
          label: item.statusName,
          value: item.statusName,
        }));
        this.brandOptions = responseData.catalogBrand.map((item) => ({
          label: item,
          value: item,
        }));
        this.brandOptions.push({ label: "Other", value: "Other" });
      } catch (error) {
        swal("Sorry", error.response.data.message, "error", {
          button: "Ok",
        }).then((v) => {
          if (error.response.data.status == 401) {
            this.handleLogout();
          }
        });
      }
    },
    getConditionList() {
      this.$http
        .get(process.env.GET_CONDITION_LIST, {
          headers: {
            "Asani-Token": localStorage.getItem("jwt"),
          },
        })
        .then((res) => {
          if (res.status == 200) {
            this.conditionOptions = res.data.content.map((item) => {
              return { label: item, value: item };
            });
          }
        })
        .catch((error) => {
          swal("Sorry", error.response.data.message, "error", {
            button: "Ok",
          }).then((v) => {
            if (error.response.data.status == 401) {
              this.handleLogout();
            }
          });
        });
    },
    getBom(catalogId) {
      this.$http
        .get(process.env.GET_CATALOG_BOM + catalogId, {
          headers: {
            "Asani-Token": localStorage.getItem("jwt"),
          },
        })
        .then((res) => {
          if (res.status == 200) {
            this.totalBom = (res.data.content && res.data.content.length) || 0;
          }
        })
        .catch((error) => {
          swal("Sorry", error.response.data.message, "error", {
            button: "Ok",
          }).then((v) => {
            if (error.response.data.status == 401) {
              this.handleLogout();
            }
          });
        });
    },
  },
  watch: {
    currentData: {
      immediate: true,
      handler(val) {
        if (val) {
          this.forms = val;
        }
      },
    },
    catalogId: {
      immediate: true,
      handler(val) {
        if (val) {
          this.getBom(val);
        }
      },
    },
  },
  mounted() {
    this.getCategory();
    this.getConditionList();
  },
};
</script>

<style scoped>
.section-title {
  font-weight: 700;
  font-size: 1.125rem;
  margin-bottom: 0.5rem;
}

.section-description {
  font-weight: 400;
  font-size: 0.875rem;
}

.insurance-wrapper {
  border: 1px solid #bbf7d0;
  background: linear-gradient(90deg, #f1fdf4 0%, #eff6ff 100%);
  padding: 1.5rem;
  border-radius: 0.5rem;
}

.hint {
  font-size: 12px;
  color: #2e3646;
  margin-top: 6px;
  margin-bottom: 0px;
}

.specification-wrapper {
  background: #f8f9fa;
  padding: 1.5rem;
  border-radius: 0.5rem;
}

.bg-white {
  background: #ffffff;
}
</style>



// WEBPACK FOOTER //
// src/components/Catalog/Components/Form.vue